"use client"

import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "./ServiceRequestForm.css"
import { supabase } from "../lib/supabase"
import { generateTimeSlots, getAvailableSlots, formatLocalDate } from "../utils/timeSlots"
import { getBookedTimeSlots } from "../lib/publicQueries"
import { useScrollToError } from "../hooks/useScrollToError"

const formSchema = z.object({
  firstName: z.string().min(2, { message: "First name is required" }),
  lastName: z.string().min(2, { message: "Last name is required" }),
  dateOfBirth: z.date({ required_error: "Date of birth is required" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().min(10, { message: "Please enter a valid phone number" }),
  service: z.enum(["pharmacy-first", "weight-loss", "new-medicine-clinic", "contraception", "flu-vaccination", "private-prescriptions", "hypertension"], {
    required_error: "Please select a service",
  }),
  preferredDate: z.date({ required_error: "Preferred date is required" }).refine(
    (date) => {
      const day = date.getDay()
      return day !== 0 && day !== 6
    },
    { message: "Appointments are only available on weekdays" }
  ),
  preferredTime: z.string().min(1, { message: "Please select a time slot" }),
  additionalNotes: z.string().optional(),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must agree to the privacy policy",
  }),
})

const validServices = ["pharmacy-first", "weight-loss", "new-medicine-clinic", "contraception", "flu-vaccination", "private-prescriptions", "hypertension"]

function ServiceRequestForm() {
  const [searchParams] = useSearchParams()
  const preselectedService = searchParams.get("service")
  const isServiceLocked = preselectedService && validServices.includes(preselectedService)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [availableSlots, setAvailableSlots] = useState([])
  const [loadingSlots, setLoadingSlots] = useState(false)

  // Scroll to top on success
  useEffect(() => {
    if (isSuccess) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [isSuccess])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      service: "",
      preferredTime: "",
      additionalNotes: "",
      consent: false,
    },
  })

  useScrollToError(errors, error)

  const dateOfBirth = watch("dateOfBirth")
  const preferredDate = watch("preferredDate")

  // Set preselected service from URL param
  useEffect(() => {
    if (isServiceLocked) {
      setValue("service", preselectedService)
    }
  }, [isServiceLocked, preselectedService, setValue])

  // Filter out weekends (Saturday = 6, Sunday = 0)
  const isWeekday = (date) => {
    const day = date.getDay()
    return day !== 0 && day !== 6
  }

  // Fetch available time slots when date changes
  useEffect(() => {
    async function fetchAvailableSlots() {
      if (!preferredDate) {
        setAvailableSlots([])
        return
      }

      setLoadingSlots(true)
      try {
        // Generate all possible time slots
        const allSlots = generateTimeSlots()

        // Fetch booked slots for the selected date
        const { data: bookedSlots, error } = await getBookedTimeSlots(preferredDate)

        if (error) {
          console.error("Error fetching booked slots:", error)
          setError("Unable to check slot availability. Showing all slots as available.")
          setAvailableSlots(allSlots) // Show all slots if there's an error
        } else {
          console.log('All time slots:', allSlots.length)
          console.log('Booked slots:', bookedSlots)
          // Filter out booked slots
          const available = getAvailableSlots(allSlots, bookedSlots || [])
          console.log('Available slots:', available.length)
          setAvailableSlots(available)

          // Clear any previous errors
          if (error && !bookedSlots) {
            setError(null)
          }
        }
      } catch (err) {
        console.error("Error loading time slots:", err)
        setError("Unable to check slot availability. Showing all slots as available.")
        setAvailableSlots(generateTimeSlots()) // Fallback to all slots
      } finally {
        setLoadingSlots(false)
      }
    }

    fetchAvailableSlots()
    // Reset time selection when date changes
    setValue("preferredTime", "")
  }, [preferredDate, setValue])

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    setError(null)

    try {
      // Transform data for database (camelCase → snake_case, Date → YYYY-MM-DD string)
      const submissionData = {
        first_name: data.firstName,
        last_name: data.lastName,
        date_of_birth: formatLocalDate(data.dateOfBirth),
        email: data.email,
        phone: data.phone,
        service: data.service,
        preferred_date: formatLocalDate(data.preferredDate),
        preferred_time: data.preferredTime,
        additional_notes: data.additionalNotes || null,
        consent: data.consent,
      }

      const { error: supabaseError } = await supabase
        .from('service_requests')
        .insert([submissionData])

      if (supabaseError) {
        throw new Error(supabaseError.message)
      }

      setIsSuccess(true)

      // Send notification emails (non-blocking — don't fail the submission if email fails)
      // Note: amendment_token will be fetched server-side by the Edge Function
      try {
        supabase.functions.invoke('send-notification-email', {
          body: {
            type: 'service_request',
            data: submissionData,
          },
        })
      } catch (emailError) {
        console.error('Email notification failed:', emailError)
      }
    } catch (err) {
      console.error("Error submitting form:", err)
      setError(
        err instanceof Error
          ? err.message
          : "An unexpected error occurred. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="success-message">
        <div className="success-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3>Service Request Submitted Successfully</h3>
        <p>Thank you for your service request. Your appointment has been booked and we'll send you a confirmation shortly.</p>
        <button
          className="button"
          onClick={() => {
            setIsSuccess(false)
            reset()
          }}
        >
          Submit Another Request
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="prescription-form">
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            id="firstName"
            type="text"
            placeholder="John"
            {...register("firstName")}
            className={errors.firstName ? "error" : ""}
          />
          {errors.firstName && <p className="error-text">{errors.firstName.message}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            id="lastName"
            type="text"
            placeholder="Smith"
            {...register("lastName")}
            className={errors.lastName ? "error" : ""}
          />
          {errors.lastName && <p className="error-text">{errors.lastName.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="dateOfBirth">Date of Birth</label>
        <DatePicker
          selected={dateOfBirth}
          onChange={(date) => setValue("dateOfBirth", date)}
          maxDate={new Date()}
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={100}
          dropdownMode="select"
          dateFormat="dd/MM/yyyy"
          className={errors.dateOfBirth ? "error" : ""}
          placeholderText="Select date of birth"
        />
        {errors.dateOfBirth && <p className="error-text">{errors.dateOfBirth.message}</p>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            type="email"
            placeholder="john.smith@example.com"
            {...register("email")}
            className={errors.email ? "error" : ""}
          />
          {errors.email && <p className="error-text">{errors.email.message}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            id="phone"
            type="tel"
            placeholder="07123 456789"
            {...register("phone")}
            className={errors.phone ? "error" : ""}
          />
          {errors.phone && <p className="error-text">{errors.phone.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="service">Select a Service</label>
        <select
          id="service"
          {...register("service")}
          className={`form-control ${isServiceLocked ? "locked" : ""}`}
          disabled={isServiceLocked}
        >
          <option value="">-- Please choose a service --</option>
          <option value="pharmacy-first">Pharmacy First (NHS)</option>
          <option value="weight-loss">Weight Loss Service</option>
          <option value="new-medicine-clinic">New Medicine Service</option>
          <option value="contraception">Contraception</option>
          <option value="flu-vaccination">Flu Vaccination</option>
          <option value="private-prescriptions">Private Prescriptions</option>
          <option value="hypertension">Hypertension Case Findings</option>
        </select>
        {isServiceLocked && <small>Service pre-selected from the previous page</small>}
        {errors.service && <p className="error-text">{errors.service.message}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="preferredDate">Preferred Appointment Date</label>
        <DatePicker
          selected={preferredDate}
          onChange={(date) => setValue("preferredDate", date)}
          minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
          filterDate={isWeekday}
          dateFormat="dd/MM/yyyy"
          className={errors.preferredDate ? "error" : ""}
          placeholderText="Select preferred date"
        />
        <small>Select a date to see available time slots (weekdays only)</small>
        {errors.preferredDate && <p className="error-text">{errors.preferredDate.message}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="preferredTime">Preferred Time Slot</label>
        <select
          id="preferredTime"
          {...register("preferredTime")}
          className={errors.preferredTime ? "error" : ""}
          disabled={!preferredDate || loadingSlots}
        >
          <option value="">
            {!preferredDate
              ? "Please select a date first"
              : loadingSlots
              ? "Loading available slots..."
              : "-- Select a time slot --"}
          </option>
          {availableSlots.length === 0 && preferredDate && !loadingSlots && (
            <option value="" disabled>
              No available slots for this date
            </option>
          )}
          {availableSlots.map((slot) => (
            <option key={slot.value} value={slot.value}>
              {slot.label}
            </option>
          ))}
        </select>
        <small>30-minute appointment slots (10:00 AM - 1:00 PM, 2:00 PM - 5:00 PM)</small>
        {errors.preferredTime && <p className="error-text">{errors.preferredTime.message}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="additionalNotes">Additional Notes or Questions</label>
        <textarea
          id="additionalNotes"
          placeholder="Any questions or additional information we should know"
          {...register("additionalNotes")}
        />
      </div>

      <div className="form-group consent-group">
        <div className="checkbox-container">
          <input type="checkbox" id="consent" {...register("consent")} className={errors.consent ? "error" : ""} />
          <label htmlFor="consent">
            I consent to Goosnargh Pharmacy processing my personal data for the purpose of providing this service
          </label>
        </div>
        <small>Your information will be handled in accordance with our privacy policy.</small>
        {errors.consent && <p className="error-text">{errors.consent.message}</p>}
      </div>

      <button type="submit" className="button" disabled={isSubmitting}>
        {isSubmitting ? "Processing..." : "Submit Service Request"}
      </button>
    </form>
  )
}

export default ServiceRequestForm
